const STORAGE_KEYS = {
  AUTH_TOKEN: '@wlb/auth_token',
  REFRESH_TOKEN: '@wlb/refresh_token',
  TOKEN_EXPIRES_AT: '@wlb/token_expires_at',
  USER_PROFILE: '@wlb/user_profile',
  USER_ID: '@wlb/user_id',
  USER_GUID: '@wlb/user_guid',
  DEVICE_ID: '@wlb/device_id',
  THEME_MODE: '@wlb/theme_mode',
  ONBOARDING_DONE: '@wlb/onboarding_done',
  AI_BUDDY_DISCLAIMER_ACCEPTED: '@wlb/ai_buddy_disclaimer_accepted',
  ROBI_QUICK_ANSWER_DISCLAIMER_ACCEPTED: '@wlb/robi_quick_answer_disclaimer_accepted',
  DONT_ASK_ROBI_QUICK_ANSWER_SHEET: 'DontAskRobiQuickAnswerSheet',
  ROBI_CONVERSATION_ID: '@wlb/robi_conversation_id',
  CONVERSATIONS: '@wlb/conversations',
  CONVERSATION_PREFS: '@wlb/conversation_prefs',
  FCM_TOKEN: '@wlb/fcm_token',
  FCM_TOKEN_TIMESTAMP: '@wlb/fcm_token_timestamp',
  NOTIFICATION_PERMISSION: '@wlb/notification_permission',
  SURVEY_DATA: '@wlb/survey_data',
  BUDDY_PREFERENCES: '@wlb/buddy_preferences',
  /** Login email convenience only — survives logout (not user profile data). */
  REMEMBER_ME_CREDENTIALS: 'REMEMBER_ME_CREDENTIALS',
  NOTIFICATION_SWIPE_INSTRUCTION_SHOWN: '@wlb/notification_swipe_instruction_shown',
  /** Persisted filter state for the Filter Posts → Hashtag tab. */
  POST_FILTER_HASHTAGS: '@wlb/post_filter_hashtags',
  /** Persisted filter state for the Filter Posts → Find My People tab. */
  POST_FILTER_PEOPLE: '@wlb/post_filter_people',
  /** One-time AppsFlyer → PostHog install attribution event (device-scoped). */
  ANALYTICS_INSTALL_ATTRIBUTED: '@wlb/analytics_install_attributed',
  /**
   * First-touch Branch attribution captured on the first Branch session
   * (device-scoped; survives logout). Minimal campaign/channel/source only.
   */
  BRANCH_FIRST_TOUCH_ATTRIBUTION: '@wlb/branch_first_touch_attribution',
  /**
   * Affiliate portal session. Deliberately outside the member session cluster:
   * an affiliate is a separate identity, so signing a member out must not sign
   * the affiliate out and vice versa.
   */
  AFFILIATE_SESSION: '@wlb/affiliate_session',
  /** Campaign slug captured by /ace, /cee; pre-fills the application's source. */
  AFFILIATE_SOURCE: '@wlb/affiliate_source',
  /** Per-user activation once-guards (cleared on logout). */
  ANALYTICS_FIRST_FEED_VIEWED: '@wlb/analytics_first_feed_viewed',
  ANALYTICS_FIRST_POST_CREATED: '@wlb/analytics_first_post_created',
  ANALYTICS_FIRST_WEIGHT_LOGGED: '@wlb/analytics_first_weight_logged',
  ANALYTICS_FIRST_ROBI_MESSAGE: '@wlb/analytics_first_robi_message',
  ANALYTICS_FIRST_LIKE: '@wlb/analytics_first_like',
  ANALYTICS_FIRST_COMMENT: '@wlb/analytics_first_comment',
} as const;

/**
 * Persisted keys that belong to the authenticated user and must be wiped on
 * logout / session expiry so the next account never sees leftover data.
 */
const USER_SESSION_STORAGE_KEYS: readonly string[] = [
  STORAGE_KEYS.AUTH_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.TOKEN_EXPIRES_AT,
  STORAGE_KEYS.USER_PROFILE,
  STORAGE_KEYS.USER_ID,
  STORAGE_KEYS.USER_GUID,
  STORAGE_KEYS.ONBOARDING_DONE,
  STORAGE_KEYS.AI_BUDDY_DISCLAIMER_ACCEPTED,
  STORAGE_KEYS.ROBI_QUICK_ANSWER_DISCLAIMER_ACCEPTED,
  STORAGE_KEYS.DONT_ASK_ROBI_QUICK_ANSWER_SHEET,
  STORAGE_KEYS.ROBI_CONVERSATION_ID,
  STORAGE_KEYS.CONVERSATIONS,
  STORAGE_KEYS.CONVERSATION_PREFS,
  STORAGE_KEYS.SURVEY_DATA,
  STORAGE_KEYS.BUDDY_PREFERENCES,
  STORAGE_KEYS.POST_FILTER_HASHTAGS,
  STORAGE_KEYS.POST_FILTER_PEOPLE,
  STORAGE_KEYS.ANALYTICS_FIRST_FEED_VIEWED,
  STORAGE_KEYS.ANALYTICS_FIRST_POST_CREATED,
  STORAGE_KEYS.ANALYTICS_FIRST_WEIGHT_LOGGED,
  STORAGE_KEYS.ANALYTICS_FIRST_ROBI_MESSAGE,
  STORAGE_KEYS.ANALYTICS_FIRST_LIKE,
  STORAGE_KEYS.ANALYTICS_FIRST_COMMENT,
];

/**
 * App / device keys that must survive logout.
 * (Remember-me email is also preserved; it is not listed here because it is
 * not part of STORAGE_KEYS' device cluster — see REMEMBER_ME_CREDENTIALS.)
 */
const APP_PERSIST_STORAGE_KEYS: readonly string[] = [
  STORAGE_KEYS.DEVICE_ID,
  STORAGE_KEYS.THEME_MODE,
  STORAGE_KEYS.FCM_TOKEN,
  STORAGE_KEYS.FCM_TOKEN_TIMESTAMP,
  STORAGE_KEYS.NOTIFICATION_PERMISSION,
  STORAGE_KEYS.REMEMBER_ME_CREDENTIALS,
  STORAGE_KEYS.ANALYTICS_INSTALL_ATTRIBUTED,
  STORAGE_KEYS.BRANCH_FIRST_TOUCH_ATTRIBUTION,
];

export { STORAGE_KEYS, USER_SESSION_STORAGE_KEYS, APP_PERSIST_STORAGE_KEYS };
